import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FaChartLine, FaRegHandshake, FaFileInvoiceDollar, FaShieldAlt } from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(-100px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(100px); }
  to { opacity: 1; transform: translateX(0); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(50px); }
  to { opacity: 1; transform: translateY(0); }
`;

const FAQContainer = styled.div`
  display: flex;
  gap: 40px;
  width: 100%;
  background: white;
  padding: 40px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
`;

const FAQContent = styled.div`
  flex: 1;
  animation: ${slideInRight} 1s ease-in-out;
`;

const FAQItem = styled.div`
  background: #ffffff;
  margin: 15px 0;
  border-bottom: 1px solid #d1d5db;
  overflow: hidden;
  transition: all 0.3s ease-in-out;
  opacity: 0;
  transform: translateY(20px);
  animation: ${fadeUp} 0.5s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};
`;

const Question = styled.div`
  background: #f9fafb;
  padding: 15px;
  font-size: 18px;
  font-weight: bold;
  color: #333;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background 0.3s ease;

  &:hover {
    background: #eef1f6;
  }
`;

const Answer = styled.div`
  padding: 15px;
  font-size: 16px;
  color: #555;
  background: #ffffff;
  border-top: 1px solid #d1d5db;
  animation: ${fadeIn} 0.5s ease-in-out;
`;

const Icon = styled.span`
  font-size: 22px;
  font-weight: bold;
  color: #E60000;
  transition: transform 0.3s ease;
  ${({ isOpen }) => isOpen && "transform: rotate(180deg);"}
`;

const LeftSection = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
  max-height: 400px;
  animation: ${slideInLeft} 1s ease-in-out;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease-in-out;
  }

  &:hover img {
    transform: scale(1.05);
  }
`;

const ExperienceBadge = styled.div`
  position: absolute;
  bottom: 20px;
  left: 20px;
  background: linear-gradient(to right, #228B22, #E60000);
  color: white;
  padding: 10px 18px;
  font-size: 18px;
  font-weight: bold;
`;

const ServiceBox = styled.div`
  background: #f7f9fa;
  border-left: 4px solid #228B22;
  padding: 20px;
  margin: 10px 0;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 5px 15px rgba(0,0,0,0.1);
    transform: translateY(-5px);
  }
`;

const WealthManagement = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const faqData = [
    { 
      question: "Qu'est-ce que la gestion de patrimoine par un expert-comptable ?", 
      answer: "C'est un accompagnement personnalisé pour optimiser et développer votre patrimoine (immobilier, financier, professionnel), en tenant compte de vos objectifs, de votre situation fiscale et des évolutions légales." 
    },
    { 
      question: "Pourquoi confier la gestion de mon patrimoine à un expert-comptable ?", 
      answer: "Un expert-comptable a une vision globale de votre situation (comptable, fiscale, juridique), vous apporte des conseils objectifs et personnalisés, et vous aide à prendre des décisions éclairées." 
    },
    { 
      question: "À qui s'adresse ce service ?", 
      answer: "Ce service s'adresse aux particuliers, aux chefs d'entreprise et aux professions libérales souhaitant optimiser leur patrimoine et préparer leur avenir." 
    },
    { 
      question: "Quels sont les objectifs de la gestion de patrimoine ?", 
      answer: "Les objectifs varient : développement du patrimoine, optimisation fiscale, préparation de la retraite, transmission du patrimoine, protection de la famille, etc." 
    },
    { 
      question: "Quels sont les services que CINEF-EC inclut dans la gestion de patrimoine ?", 
      answer: "Nous proposons un bilan patrimonial complet, des conseils en investissement, une optimisation fiscale, une préparation de la retraite, une planification successorale et un suivi régulier de votre patrimoine." 
    },
    { 
      question: "Pouvez-vous m'aider à optimiser ma fiscalité patrimoniale ?", 
      answer: "Oui, nous analysons votre situation fiscale et vous conseillons sur les stratégies d'optimisation légales (placements, dispositifs de défiscalisation, etc.)." 
    },
    { 
      question: "Accompagnez-vous la transmission de patrimoine ?", 
      answer: "Oui, nous vous conseillons sur les donations, les successions et les stratégies pour protéger votre famille." 
    }
  ];

  const services = [
    {
      title: "Bilan patrimonial complet",
      description: "Analyse de votre situation patrimoniale (actifs, passifs, revenus, charges) et définition de vos objectifs."
    },
    {
      title: "Conseils en investissement",
      description: "Sélection de placements adaptés à votre profil de risque et à vos objectifs de rendement."
    },
    {
      title: "Optimisation fiscale",
      description: "Conseils sur les stratégies d'optimisation fiscale (placements, dispositifs de défiscalisation, etc.)."
    },
    {
      title: "Préparation de la retraite",
      description: "Évaluation de vos besoins, choix des produits d'épargne adaptés (assurance-vie, etc.)."
    },
    {
      title: "Planification successorale",
      description: "Conseils sur les donations, les successions et les stratégies pour protéger votre famille."
    },
    {
      title: "Suivi régulier",
      description: "Suivi de l'évolution de votre patrimoine et ajustement des stratégies en fonction de vos objectifs et de la conjoncture."
    }
  ];

  return (
    <>
      <Header />
      
      <section className="service-banner d-flex align-items-center">
        <div className="container text-white text-start">
          <h1 className="fw-bold">Gestion du Patrimoine</h1>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/" className="text-white text-decoration-none">
                  Accueil
                </Link>{" "}
                /{" "}
                <Link to="/services" className="text-white text-decoration-none">
                  Services
                </Link>
              </li>
              <li className="breadcrumb-item active text-white" aria-current="page">
                Gestion du Patrimoine
              </li>
            </ol>
          </nav>
        </div>
      </section>

      <section className="service-content py-5">
        <div className="container">
          <div className="row">
            

            <div className="col-lg-12">
              <h2 className="fw-bold">Gestion du Patrimoine</h2>
              <p className="text-muted">
                La gestion de votre patrimoine est un enjeu majeur pour votre sérénité financière et celle de votre famille. 
                CINEF-EC vous propose un accompagnement personnalisé pour optimiser et développer votre patrimoine, 
                en tenant compte de vos objectifs et de votre situation spécifique.
              </p>
              <h4 className="fw-bold mt-4">Pourquoi nous confier la gestion de votre patrimoine ?</h4>
              <ul className="text-muted">
                <li><strong>Expertise globale :</strong> Nous combinons notre expertise comptable, fiscale et juridique pour vous offrir une vision complète de votre situation patrimoniale.</li>
                <li><strong>Conseils personnalisés :</strong> Nous vous apportons des conseils sur mesure, adaptés à vos objectifs (préparation de la retraite, transmission du patrimoine, optimisation fiscale, etc.).</li>
                <li><strong>Indépendance et objectivité :</strong> Nous vous conseillons en toute indépendance, en privilégiant vos intérêts.</li>
                <li><strong>Confiance et confidentialité :</strong> Nous respectons scrupuleusement la confidentialité de vos informations et nous construisons une relation de confiance durable.</li>
                <li><strong>Veille juridique et fiscale :</strong> Nous assurons une veille permanente pour vous informer des évolutions législatives et fiscales ayant un impact sur votre patrimoine.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="advantages py-5 text-center">
        <div className="container">
          <h3 className="fw-bold">Nos Expertises en Gestion Patrimoniale</h3>
          <div className="row mt-4">
            <div className="col-md-3">
              <FaChartLine className="advantage-icon text-primary" />
              <p className="fw-bold">Optimisation Fiscale</p>
            </div>
            <div className="col-md-3">
              <FaRegHandshake className="advantage-icon text-success" />
              <p className="fw-bold">Transmission Patrimoniale</p>
            </div>
            <div className="col-md-3">
              <FaFileInvoiceDollar className="advantage-icon text-danger" />
              <p className="fw-bold">Investissements</p>
            </div>
            <div className="col-md-3">
              <FaShieldAlt className="advantage-icon text-warning" />
              <p className="fw-bold">Protection Familiale</p>
            </div>
          </div>
        </div>
      </section>

      <section className="services-detail py-5 bg-light">
        <div className="container">
          <h3 className="text-center fw-bold mb-4">Nos Services</h3>
          <div className="row">
            {services.map((service, index) => (
              <div className="col-md-6" key={index}>
                <ServiceBox>
                  <h5 className="fw-bold">{service.title}</h5>
                  <p className="mb-0 text-muted">{service.description}</p>
                </ServiceBox>
              </div>
            ))}
          </div>
          <div className="text-center mt-4">
            <p className="lead fw-bold">Votre patrimoine, notre priorité</p>
            <p className="text-muted">
              Nous vous accompagnons dans toutes les étapes de la gestion de votre patrimoine, pour vous permettre 
              de réaliser vos projets et de préparer votre avenir en toute sérénité. 
              Contactez-nous dès aujourd'hui pour une première consultation gratuite.
            </p>
          </div>
        </div>
      </section>

      <section id="faq-section" className="container-fluid py-5">
        <div className="container">
          <h3 className="text-center fw-bold mb-5">Questions Fréquentes</h3>
          <FAQContainer>
           
            
            <FAQContent>
              {faqData.map((faq, index) => (
                <FAQItem key={index} delay={`${index * 0.2}s`}>
                  <Question onClick={() => toggleFAQ(index)}>
                    {faq.question}
                    <Icon isOpen={activeIndex === index}>{activeIndex === index ? "−" : "+"}</Icon>
                  </Question>
                  {activeIndex === index && <Answer>{faq.answer}</Answer>}
                </FAQItem>
              ))}
            </FAQContent>
          </FAQContainer>
        </div>
      </section>

      <Footer />

      <style>{`
        .service-banner {
          background: linear-gradient(to right, rgba(0, 138, 68, 0.8), rgba(220, 53, 69, 0.8)), url("/assets/images/service-banner.jpg");
          background-size: cover;
          background-position: center;
          height: 400px;
          display: flex;
          align-items: center;
        }
        
        .advantage-icon {
          font-size: 2.5rem;
          margin-bottom: 1rem;
        }
      `}</style>
    </>
  );
};

export default WealthManagement;